/* eslint-disable react/prop-types, react/destructuring-assignment */
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { FormattedMessage, injectIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath, matchPath, useHistory, useLocation } from 'react-router-dom'
import DropdownMenu from '../../../app/components/DropdownMenu'
import FormButtons from '../../../app/components/form/Form/Buttons/Buttons'
import useAuthorization from '../../../app/hooks/useAuthorization'
import {
  EMPLOYEES_INTEGRATION_TOGGLE_EVENT,
  EmployeesIntegrationToggleEventEmitter,
} from '../../../app/pages/home/Employees/EmployeesIntegrationToggleEventEmitter'
import { MODAL_STATES as POLICY_TEMPLATE_GROUP_STATES } from '../../../app/pages/home/secure/policy_templates/TemplateGroupModal'
import CreatePostureTemplateButton from '../../../app/pages/home/secure/posture_templates/CreatePostureTemplateButton'
import { EXPORTABLE_POSTURE_CHECK_IDS } from '../../../app/pages/home/secure/security_posture/export/constants'
import BetaTag from '../../../app/partials/content/BetaTag'
import LegacyTag from '../../../app/partials/content/LegacyTag'
import DatePicker from '../../../app/partials/layout/DatePicker'
import {
  AI_RISK_REPORT_PATH,
  BETA_AI_RISK_REPORT_PATH,
  GROUPS_PATH,
  GROUPS_ROOT_PATH,
  INTUNE_APP_CONFIGURATION_PATH,
  INTUNE_APP_CONFIGURATION_TEMPLATES_PATH,
  INTUNE_APP_PROTECTION_PATH,
  INTUNE_APP_PROTECTION_TEMPLATES_PATH,
  INTUNE_APPS_PATH,
  INTUNE_APPS_TEMPLATES_PATH,
  INTUNE_DEVICE_COMPLIANCE_PATH,
  INTUNE_DEVICE_COMPLIANCE_TEMPLATES_PATH,
  INTUNE_DEVICE_CONFIGURATION_PATH,
  INTUNE_DEVICE_CONFIGURATION_TEMPLATES_PATH,
  INTUNE_REMEDIATION_SCRIPTS_PATH,
  INTUNE_REMEDIATION_SCRIPTS_TEMPLATES_PATH,
  INTUNE_SCRIPTS_PATH,
  INTUNE_SCRIPTS_TEMPLATES_PATH,
  PURVIEW_DLP_PATH,
  PURVIEW_DLP_TEMPLATES_PATH,
  PURVIEW_RETENTION_PATH,
  PURVIEW_RETENTION_TEMPLATES_PATH,
  SECURE_COMPLIANCE_ASSESSMENT_PATH,
  SECURE_COMPLIANCE_ASSESSMENTS_PATH,
} from '../../../app/router/constants'
import { isComplianceAuditRoutePath } from '../../../app/router/utils'
import AIRiskReportExportWidget from '../../../app/widgets/AIRiskReportExportWidget'
import ExportDiscoverComplianceWidget from '../../../app/widgets/ExportDiscoverComplianceWidget'
import ExportLicenseWidget from '../../../app/widgets/ExportLicenseWidget'
import ExportWidget from '../../../app/widgets/ExportWidget'
import * as subheader from '../../ducks/subheader'
import { getSelectedCustomerId } from '../../selectors/customers'
import { useFeatureFlag } from '../../services/featureFlags'
import { isSyncPath } from '../../utils/utils'
import { LayoutContextConsumer } from '../LayoutContext'
import AddApplicationButton from './components/AddApplicationButton'
import AddEmployeesButton from './components/AddEmployeesButton'
import CustomizeAuditButton from './components/CustomizeAuditButton'
import ImportSettingsButton from './components/ImportSettingsButton'
import IntegrationRefreshButton from './components/IntegrationRefreshButton'
import IntegrationsButton from './components/IntegrationsButton'

const MicrosoftOffice = 'Office365'
const AI_RISK_REPORT_PATHS = [AI_RISK_REPORT_PATH, BETA_AI_RISK_REPORT_PATH]

function SubHeader(props) {
  const { subheaderCssClasses, subheaderContainerCssClasses, subheaderMobileToggle, isChildLoading } = props

  const dispatch = useDispatch()

  const location = useLocation()
  const history = useHistory()
  const llmChatFeatureEnabled = useFeatureFlag('augmentt-ai-assistant')

  const selectedCustomerId = useSelector(getSelectedCustomerId)
  const permissions = useSelector(({ userPermissions }) => userPermissions.data.permissions)
  const showSecurityChecksPage = useSelector(({ secure }) => secure.showSecurityChecksPage)
  const selectedIntegration = useSelector(({ integrations }) => integrations.selectedIntegration)
  const selectedIntegrationType = selectedIntegration?.IntegrationApplication?.application_name
  const isGWSIntegration = selectedIntegrationType === 'GWS'
  const integrationAlertFailed = useSelector(({ subheader: sh }) => sh.integrationAlertFailed === true)
  const securePremiumEnabled = useSelector(({ meta }) => meta.securePremiumEnabled)

  const employeeEventEmitter = (data) => {
    // eslint-disable-next-line no-use-before-define
    setShowImportSettingsButton(data.selectedIntegrationType === MicrosoftOffice)
  }

  const [showImportSettingsButton, setShowImportSettingsButton] = useState(false) // disabled by default

  const { isAuthorizedForEngageAutopilot, isAuthorizedForIntune, isAuthorizedForPurview } = useAuthorization()

  useEffect(() => {
    EmployeesIntegrationToggleEventEmitter.on(EMPLOYEES_INTEGRATION_TOGGLE_EVENT, employeeEventEmitter)
    return () => {
      EmployeesIntegrationToggleEventEmitter.off(EMPLOYEES_INTEGRATION_TOGGLE_EVENT, employeeEventEmitter)
    }
  })

  const startOfDay = { hour: 0, minute: 0, second: 0, millisecond: 0 }
  const endOfDay = { hour: 23, minute: 59, second: 59, millisecond: 999 }
  const isExportablePostureCheckDetail =
    showSecurityChecksPage?.checkId && EXPORTABLE_POSTURE_CHECK_IDS.has(showSecurityChecksPage.checkId)
  const isAiRiskReportPath = AI_RISK_REPORT_PATHS.some((path) => matchPath(location.pathname, { path, exact: true }))

  const onContactSales = async () => {
    window.location = 'mailto:sales@augmentt.com?subject=Licensing Inquiry'
  }
  const customizeAuditCondition =
    window.location.pathname.match(/security-posture/) && permissions.secure === 'manage' && securePremiumEnabled

  return (
    <div id="am_subheader" className={`subheader ${subheaderCssClasses} grid__item`}>
      <div className={`container ${subheaderContainerCssClasses}`}>
        <div className="subheader__main">
          {subheaderMobileToggle && (
            // eslint-disable-next-line jsx-a11y/control-has-associated-label
            <button
              type="button"
              className="subheader__mobile-toggle subheader__mobile-toggle--left"
              id="am_subheader_mobile_toggle"
            >
              <span />
            </button>
          )}

          <LayoutContextConsumer>
            {({ subheader: { title, beta, legacy } }) => (
              <>
                <h3 className="subheader__title">{title}</h3>
                {beta && <BetaTag className="ms-3" />}
                {legacy && <LegacyTag className="ms-3" />}
              </>
            )}
          </LayoutContextConsumer>
        </div>
        <div className="subheader__toolbar">
          <div className="subheader__wrapper">
            {isSyncPath() && <IntegrationRefreshButton />}

            {customizeAuditCondition && <CustomizeAuditButton />}
            {(window.location.pathname.match(/discovery/) || window.location.pathname.match(/discover\/risk/)) && (
              <DatePicker
                showCustomRangeLabel={false}
                dateRanges={{
                  'This Month (Past 30 days)': [moment().subtract(1, 'month').set(startOfDay), moment().set(endOfDay)],
                  'Last Month (30-60 days ago)': [
                    moment().subtract(2, 'month').set(startOfDay),
                    moment().subtract(1, 'month').set(endOfDay),
                  ],
                  '2 Months Ago (60-90 days ago)': [
                    moment().subtract(3, 'month').set(startOfDay),
                    moment().subtract(2, 'month').set(endOfDay),
                  ],
                }}
              />
            )}

            {window.location.pathname.match(/employees/) && showImportSettingsButton && <ImportSettingsButton />}

            {window.location.pathname.match(/employees/) && <AddEmployeesButton />}

            {/* If your page needs to support exporting. put your path below, and do the redux exportData implementation, see examples */}
            {(window.location.pathname.match(/discovery/) || window.location.pathname.match(/employees/)) && (
              <ExportWidget />
            )}

            {window.location.pathname.match(/auditlogs/) && <ExportWidget />}

            {window.location.pathname.match(/mfa/) && selectedCustomerId && <ExportWidget />}

            {window.location.pathname.match(/discover\/risk/) && selectedCustomerId && (
              <ExportDiscoverComplianceWidget />
            )}

            {window.location.pathname.match(/reports\/licensing(\/)?$/) && (
              <>
                <DatePicker
                  showCustomRangeLabel
                  dateRanges={{
                    Today: [moment().set(startOfDay), moment().set(endOfDay)],
                    'Last Month': [
                      moment().utc().endOf('month').subtract(2, 'months').endOf('month'),
                      moment().subtract(1, 'months').endOf('month'),
                    ],
                    '2 Months Ago': [
                      moment().utc().endOf('month').subtract(3, 'months').endOf('month'),
                      moment().subtract(2, 'months').endOf('month'),
                    ],
                    '3 Months Ago': [
                      moment().utc().endOf('month').subtract(4, 'months').endOf('month'),
                      moment().subtract(3, 'months').endOf('month'),
                    ],
                  }}
                />

                {selectedCustomerId && <ExportLicenseWidget />}
              </>
            )}

            {window.location.pathname.match(/applications/) && <AddApplicationButton />}
            {window.location.pathname.match(/engage\/user-management/) && (
              <>
                <IntegrationsButton isChildLoading={isChildLoading} />
                {isAuthorizedForEngageAutopilot && (
                  <Button
                    variant="secondary"
                    className="me-3"
                    onClick={() => dispatch(subheader.actions.setGlobalValue({ showEngageDefaultSettings: true }))}
                  >
                    <i className="fas fa-gear" />
                    <FormattedMessage id="DEFAULT_SETTINGS.DEFAULT_SETTINGS" />
                  </Button>
                )}
              </>
            )}

            {window.location.pathname.match(/engage\/action-log/) && (
              <IntegrationsButton isChildLoading={isChildLoading} integrationsAllowed={[MicrosoftOffice]} />
            )}

            {window.location.pathname.match(/engage\/distribution-lists/) && (
              <>
                <IntegrationsButton isChildLoading={isChildLoading} integrationsAllowed={[MicrosoftOffice]} disabled />
                {permissions.engage === 'manage' &&
                  (integrationAlertFailed ? (
                    <OverlayTrigger
                      overlay={
                        <Tooltip>
                          <FormattedMessage id="ENGAGE.INSUFFICIENT_PERMISSIONS" />
                        </Tooltip>
                      }
                      placement="bottom"
                    >
                      <span className="d-inline-block">
                        <Button variant="primary" disabled>
                          <i className="fa fa-plus me-2" />
                          <FormattedMessage id="GENERAL.CREATE" />
                        </Button>
                      </span>
                    </OverlayTrigger>
                  ) : (
                    <Button
                      onClick={() =>
                        dispatch(
                          subheader.actions.setGlobalValue({
                            openCreateDistributionListModal: true,
                          }),
                        )
                      }
                      variant="primary"
                      disabled={isGWSIntegration}
                    >
                      <i className="fa fa-plus me-2" />
                      <FormattedMessage id="GENERAL.CREATE" />
                    </Button>
                  ))}
              </>
            )}

            {/* For secure pages show integration selector, but only show microsoft office */}
            {window.location.pathname.match(/secure/) && !window.location.pathname.match(/assessment/) && (
              <IntegrationsButton isChildLoading={isChildLoading} integrationsAllowed={[MicrosoftOffice]} isSecure />
            )}
            {/* Secure Threat report has date picker */}
            {window.location.pathname.match(/threats/) && (
              <>
                <DatePicker
                  showCustomRangeLabel={false}
                  dateRanges={{
                    'Last 1 Month': [moment().subtract(1, 'month').set(startOfDay), moment().set(endOfDay)],
                    'Last 2 Months': [moment().subtract(2, 'month').set(startOfDay), moment().set(endOfDay)],
                    'Last 3 Months': [moment().subtract(3, 'month').set(startOfDay), moment().set(endOfDay)],
                  }}
                />
                <ExportWidget />
              </>
            )}
            {/* Secure summary report has date picker */}
            {window.location.pathname.match(/summary/) && (
              <>
                <DatePicker
                  showCustomRangeLabel={false}
                  dateRanges={{
                    'Last 1 Month': [moment().subtract(1, 'month').set(startOfDay), moment().set(endOfDay)],
                    'Last 2 Months': [moment().subtract(2, 'month').set(startOfDay), moment().set(endOfDay)],
                    'Last 3 Months': [moment().subtract(3, 'month').set(startOfDay), moment().set(endOfDay)],
                  }}
                />
                <ExportWidget />
              </>
            )}
            {/* Secure summary report has date picker */}
            {window.location.pathname.match(/alerts/) && (
              <>
                {permissions.secure === 'manage' && (
                  <Button
                    onClick={() =>
                      dispatch(
                        subheader.actions.setGlobalValue({
                          showEventSettingsModal: true,
                        }),
                      )
                    }
                    id="eventSettingsBtn"
                    variant="light"
                  >
                    <FormattedMessage id="THREAT_ALERTS.SAFE_LOCATIONS" />
                  </Button>
                )}

                <DatePicker
                  showCustomRangeLabel={false}
                  dateRanges={{
                    'Last 48 Hours': [moment().subtract(2, 'day').set(startOfDay), moment().set(endOfDay)],
                    'Last 1 Month': [moment().subtract(1, 'month').set(startOfDay), moment().set(endOfDay)],
                    'Last 2 Months': [moment().subtract(2, 'month').set(startOfDay), moment().set(endOfDay)],
                    'Last 3 Months': [moment().subtract(3, 'month').set(startOfDay), moment().set(endOfDay)],
                  }}
                />
              </>
            )}

            {window.location.pathname.match(/licenses/) && (
              <Button onClick={() => onContactSales()} id="contactBtn" variant="primary">
                <FormattedMessage id="GENERAL.CONTACT_SALES" />
              </Button>
            )}
            {window.location.pathname.match(/security-posture/) &&
              selectedCustomerId &&
              (!showSecurityChecksPage || isExportablePostureCheckDetail) && <ExportWidget />}
            {isComplianceAuditRoutePath(location.pathname) &&
              selectedCustomerId &&
              permissions.secure !== 'disabled' && <ExportWidget />}
            {isAiRiskReportPath && selectedCustomerId && <AIRiskReportExportWidget />}
            {window.location.pathname.match(/posture-templates$/) && <CreatePostureTemplateButton />}

            {window.location.pathname.match(/development/) && llmChatFeatureEnabled && (
              <Button
                onClick={() => dispatch(subheader.actions.setGlobalValue({ activateChat: true }))}
                id="chatButton"
                variant="secondary"
              >
                <FormattedMessage id="AI.CHAT.ASSISTANT" /> <i className="icon fas fa-comments ms-1 me-0 pe-0" />
              </Button>
            )}

            {window.location.pathname.match(/policy-management\/templates/) && permissions.secure === 'manage' && (
              <DropdownMenu
                id="createNewDropdown"
                title="SECURE.CREATE_NEW"
                items={[
                  {
                    id: 'createNewTemplate',
                    label: 'POLICY_TEMPLATES.POLICY_TEMPLATE',
                    onClick: () => dispatch(subheader.actions.setGlobalValue({ createNewPolicy: true })),
                  },
                  {
                    id: 'createNewTemplateGroup',
                    label: 'POLICY_TEMPLATES.BASELINE',
                    onClick: () =>
                      dispatch(
                        subheader.actions.setGlobalValue({
                          openPolicyTemplateGroupModal: POLICY_TEMPLATE_GROUP_STATES.CREATE,
                        }),
                      ),
                  },
                ]}
              />
            )}

            {window.location.pathname.match(/policy-management\/policies/) && permissions.secure === 'manage' && (
              <Button
                variant="primary"
                onClick={() => dispatch(subheader.actions.setGlobalValue({ createNewPolicy: true }))}
              >
                <FormattedMessage id="POLICY_TEMPLATES.DEPLOY_POLICY" />
              </Button>
            )}

            {window.location.pathname.match(/policy-management\/named-locations/) &&
              permissions.secure === 'manage' && (
                <DropdownMenu
                  id="createNewDropdown"
                  title="POLICY_NAMED_LOCATIONS.CREATE_NAMED_LOCATIONS"
                  items={[
                    {
                      id: 'createNewCountryLocation',
                      label: 'POLICY_NAMED_LOCATIONS.COUNTRY_LOCATION',
                      onClick: () => dispatch(subheader.actions.setGlobalValue({ createNewRiskyCountry: true })),
                    },
                    {
                      id: 'createNewIpRangesLocation',
                      label: 'POLICY_NAMED_LOCATIONS.IP_RANGES_LOCATION',
                      onClick: () => dispatch(subheader.actions.setGlobalValue({ createNewRiskyIp: true })),
                    },
                  ]}
                />
              )}

            {window.location.pathname.match(/defender\/policies/) && permissions.secure === 'manage' && (
              <DropdownMenu
                id="deploy-defender-policies-menu"
                title="POLICY_TEMPLATES.DEPLOY"
                items={[
                  {
                    id: 'deploy-defender-policy-template',
                    label: 'POLICY_TEMPLATES.POLICY',
                    onClick: () => dispatch(subheader.actions.setGlobalValue({ openDeployDefenderPolicyModal: true })),
                  },
                  {
                    id: 'deploy-defender-policy-baseline',
                    label: 'POLICY_TEMPLATES.BASELINE',
                    onClick: () =>
                      dispatch(subheader.actions.setGlobalValue({ openDeployDefenderBaselineModal: true })),
                  },
                ]}
              />
            )}

            {window.location.pathname.match(/defender\/templates/) && permissions.secure === 'manage' && (
              <DropdownMenu
                id="defender-templates-create-new-dropdown"
                title="SECURE.CREATE_NEW"
                items={[
                  {
                    id: 'create-new-defender-template',
                    label: 'POLICY_TEMPLATES.POLICY_TEMPLATE',
                    onClick: () => dispatch(subheader.actions.setGlobalValue({ openCreateNewDefenderTemplate: true })),
                  },
                  {
                    id: 'create-new-defender-template-group',
                    label: 'POLICY_TEMPLATES.BASELINE',
                    onClick: () =>
                      dispatch(
                        subheader.actions.setGlobalValue({
                          openCreateNewDefenderBaseline: true,
                        }),
                      ),
                  },
                ]}
              />
            )}

            {matchPath(location.pathname, { path: [INTUNE_DEVICE_CONFIGURATION_PATH] }) &&
              permissions.intune === 'manage' &&
              isAuthorizedForIntune && (
                <DropdownMenu
                  id="deploy-intune-device-configuration-policy-menu"
                  title="POLICY_TEMPLATES.DEPLOY"
                  items={[
                    {
                      id: 'deploy-intune-device-configuration-policy-template',
                      label: 'POLICY_TEMPLATES.POLICY',
                      onClick: () =>
                        dispatch(subheader.actions.setGlobalValue({ openDeployDeviceConfigPolicyModal: true })),
                    },
                    {
                      id: 'deploy-intune-device-configuration-policy-baseline',
                      label: 'POLICY_TEMPLATES.BASELINE',
                      onClick: () =>
                        dispatch(subheader.actions.setGlobalValue({ openDeployDeviceConfigBaselineModal: true })),
                    },
                  ]}
                />
              )}

            {matchPath(location.pathname, { path: [INTUNE_DEVICE_CONFIGURATION_TEMPLATES_PATH] }) &&
              permissions.intune === 'manage' && (
                <DropdownMenu
                  id="intune-device-configuration-templates-create-new-dropdown"
                  title="SECURE.CREATE_NEW"
                  items={[
                    {
                      id: 'create-new-intune-device-configuration-template',
                      label: 'POLICY_TEMPLATES.TEMPLATE',
                      onClick: () =>
                        dispatch(subheader.actions.setGlobalValue({ openCreateIntuneDeviceConfigTemplate: true })),
                    },
                    {
                      id: 'create-new-intune-device-template-configuration-group',
                      label: 'POLICY_TEMPLATES.BASELINE',
                      onClick: () =>
                        dispatch(
                          subheader.actions.setGlobalValue({
                            openCreateIntuneDeviceConfigBaseline: true,
                          }),
                        ),
                    },
                  ]}
                />
              )}

            {matchPath(location.pathname, { path: [INTUNE_DEVICE_COMPLIANCE_PATH] }) &&
              permissions.intune === 'manage' &&
              isAuthorizedForIntune && (
                <DropdownMenu
                  id="deploy-intune-device-compliance-policy-menu"
                  title="POLICY_TEMPLATES.DEPLOY"
                  items={[
                    {
                      id: 'deploy-intune-device-compliance-policy-template',
                      label: 'POLICY_TEMPLATES.POLICY',
                      onClick: () =>
                        dispatch(subheader.actions.setGlobalValue({ openDeployDeviceCompliancePolicyModal: true })),
                    },
                    {
                      id: 'deploy-intune-device-compliance-policy-baseline',
                      label: 'POLICY_TEMPLATES.BASELINE',
                      onClick: () =>
                        dispatch(subheader.actions.setGlobalValue({ openDeployDeviceComplianceBaselineModal: true })),
                    },
                  ]}
                />
              )}

            {matchPath(location.pathname, { path: [INTUNE_DEVICE_COMPLIANCE_TEMPLATES_PATH] }) &&
              permissions.intune === 'manage' && (
                <DropdownMenu
                  id="intune-device-compliance-templates-create-new-dropdown"
                  title="SECURE.CREATE_NEW"
                  items={[
                    {
                      id: 'create-new-intune-device-compliance-template',
                      label: 'POLICY_TEMPLATES.TEMPLATE',
                      onClick: () =>
                        dispatch(subheader.actions.setGlobalValue({ openCreateIntuneDeviceComplianceTemplate: true })),
                    },
                    {
                      id: 'create-new-intune-device-template-compliance-group',
                      label: 'POLICY_TEMPLATES.BASELINE',
                      onClick: () =>
                        dispatch(
                          subheader.actions.setGlobalValue({
                            openCreateIntuneDeviceComplianceBaseline: true,
                          }),
                        ),
                    },
                  ]}
                />
              )}

            {window.location.pathname.match(/secure\/intune\/onboarding\/deployment-profiles/) &&
              permissions.intune === 'manage' &&
              isAuthorizedForIntune && (
                <DropdownMenu
                  id="deployment-profile-create-menu"
                  title="POLICY_TEMPLATES.DEPLOY"
                  items={[
                    {
                      id: 'create-deployment-profile-template',
                      label: 'POLICY_TEMPLATES.POLICY',
                      onClick: () =>
                        dispatch(subheader.actions.setGlobalValue({ openCreateDeploymentProfilePolicyModal: true })),
                    },
                    {
                      id: 'create-deployment-profile-baseline',
                      label: 'POLICY_TEMPLATES.BASELINE',
                      onClick: () =>
                        dispatch(subheader.actions.setGlobalValue({ openCreateDeploymentProfileBaselineModal: true })),
                    },
                  ]}
                />
              )}

            {window.location.pathname.match(/secure\/intune\/onboarding\/templates/) &&
              permissions.intune === 'manage' && (
                <DropdownMenu
                  id="deployment-profile-templates-create-new-dropdown"
                  title="SECURE.CREATE_NEW"
                  items={[
                    {
                      id: 'create-new-deployment-profile-template',
                      label: 'POLICY_TEMPLATES.TEMPLATE',
                      onClick: () =>
                        dispatch(subheader.actions.setGlobalValue({ openCreateDeploymentProfileTemplate: true })),
                    },
                    {
                      id: 'create-new-deployment-profile-template-group',
                      label: 'POLICY_TEMPLATES.BASELINE',
                      onClick: () =>
                        dispatch(
                          subheader.actions.setGlobalValue({
                            openCreateDeploymentProfileBaseline: true,
                          }),
                        ),
                    },
                  ]}
                />
              )}

            {matchPath(location.pathname, { path: [GROUPS_ROOT_PATH, GROUPS_PATH] }) &&
              permissions.secure === 'manage' && (
                <DropdownMenu
                  id="deploy-group-menu"
                  title="GROUPS.ADD_GROUPS"
                  items={[
                    {
                      id: 'create-group-new',
                      label: 'GROUPS.CREATE_NEW',
                      onClick: () => dispatch(subheader.actions.setGlobalValue({ openCreateGroupModal: true })),
                    },
                    {
                      id: 'deploy-group-template',
                      label: 'GROUPS.FROM_TEMPLATE',
                      onClick: () => dispatch(subheader.actions.setGlobalValue({ openDeployGroupTemplateModal: true })),
                    },
                    {
                      id: 'deploy-group-baseline',
                      label: 'GROUPS.FROM_BASELINE',
                      onClick: () => dispatch(subheader.actions.setGlobalValue({ openDeployGroupBaselineModal: true })),
                    },
                  ]}
                />
              )}

            {window.location.pathname.match(/secure\/entra-id\/groups-templates/) &&
              permissions.secure === 'manage' && (
                <DropdownMenu
                  id="groups-templates-create-new-dropdown"
                  title="SECURE.CREATE_NEW"
                  items={[
                    {
                      id: 'create-new-groups-template',
                      label: 'POLICY_TEMPLATES.TEMPLATE',
                      onClick: () => dispatch(subheader.actions.setGlobalValue({ openCreateGroupsTemplate: true })),
                    },
                    {
                      id: 'create-new-groups-template-baseline',
                      label: 'POLICY_TEMPLATES.BASELINE',
                      onClick: () =>
                        dispatch(
                          subheader.actions.setGlobalValue({
                            openCreateGroupsBaseline: true,
                          }),
                        ),
                    },
                  ]}
                />
              )}

            {matchPath(location.pathname, { path: [INTUNE_APP_PROTECTION_PATH] }) &&
              permissions.intune === 'manage' && (
                <DropdownMenu
                  id="app-protection-create-menu"
                  title="POLICY_TEMPLATES.DEPLOY"
                  items={[
                    {
                      id: 'deploy-app-protection-template',
                      label: 'POLICY_TEMPLATES.POLICY',
                      onClick: () =>
                        dispatch(subheader.actions.setGlobalValue({ openAppProtectionPolicyDeploymentModal: true })),
                    },
                    {
                      id: 'deploy-app-protection-baseline',
                      label: 'POLICY_TEMPLATES.BASELINE',
                      onClick: () =>
                        dispatch(subheader.actions.setGlobalValue({ openAppProtectionBaselineDeploymentModal: true })),
                    },
                  ]}
                />
              )}

            {matchPath(location.pathname, { path: [PURVIEW_RETENTION_PATH] }) &&
              permissions.purview === 'manage' &&
              isAuthorizedForPurview &&
              selectedCustomerId && (
                <DropdownMenu
                  id="retention-create-menu"
                  title="SECURE.CREATE_NEW"
                  items={[
                    {
                      id: 'create-retention-policy',
                      label: 'GROUPS.CREATE_NEW',
                      onClick: () =>
                        dispatch(subheader.actions.setGlobalValue({ openCreateRetentionPolicyModal: true })),
                    },
                    {
                      id: 'deploy-retention-template',
                      label: 'GROUPS.FROM_TEMPLATE',
                      onClick: () =>
                        dispatch(subheader.actions.setGlobalValue({ openRetentionPolicyDeploymentModal: true })),
                    },
                    {
                      id: 'deploy-retention-baseline',
                      label: 'GROUPS.FROM_BASELINE',
                      onClick: () =>
                        dispatch(subheader.actions.setGlobalValue({ openRetentionBaselineDeploymentModal: true })),
                    },
                  ]}
                />
              )}

            {matchPath(location.pathname, { path: [INTUNE_APPS_PATH] }) && permissions.intune === 'manage' && (
              <DropdownMenu
                id="apps-create-menu"
                title="POLICY_TEMPLATES.DEPLOY"
                items={[
                  {
                    id: 'deploy-apps-template',
                    label: 'INTUNE.APP.APP',
                    onClick: () =>
                      dispatch(subheader.actions.setGlobalValue({ openAppsMobileAppDeploymentModal: true })),
                  },
                  {
                    id: 'deploy-apps-baseline',
                    label: 'POLICY_TEMPLATES.BASELINE',
                    onClick: () =>
                      dispatch(subheader.actions.setGlobalValue({ openAppsBaselineDeploymentModal: true })),
                  },
                ]}
              />
            )}

            {matchPath(location.pathname, { path: [INTUNE_SCRIPTS_PATH] }) && permissions.intune === 'manage' && (
              <DropdownMenu
                id="scripts-create-menu"
                title="POLICY_TEMPLATES.DEPLOY"
                items={[
                  {
                    id: 'deploy-scripts-template',
                    label: 'INTUNE.SCRIPT.SCRIPT',
                    onClick: () => dispatch(subheader.actions.setGlobalValue({ openScriptsDeploymentModal: true })),
                  },
                  {
                    id: 'deploy-scripts-baseline',
                    label: 'POLICY_TEMPLATES.BASELINE',
                    onClick: () =>
                      dispatch(subheader.actions.setGlobalValue({ openScriptsBaselineDeploymentModal: true })),
                  },
                ]}
              />
            )}

            {matchPath(location.pathname, { path: [INTUNE_REMEDIATION_SCRIPTS_PATH] }) &&
              permissions.intune === 'manage' && (
                <DropdownMenu
                  id="remediation-scripts-create-menu"
                  title="POLICY_TEMPLATES.DEPLOY"
                  items={[
                    {
                      id: 'deploy-remediation-script-template',
                      label: 'INTUNE.REMEDIATION_SCRIPT.REMEDIATION_SCRIPT',
                      onClick: () =>
                        dispatch(subheader.actions.setGlobalValue({ openRemediationScriptDeploymentModal: true })),
                    },
                    {
                      id: 'deploy-remediation-scripts-baseline',
                      label: 'POLICY_TEMPLATES.BASELINE',
                      onClick: () =>
                        dispatch(
                          subheader.actions.setGlobalValue({ openRemediationScriptBaselineDeploymentModal: true }),
                        ),
                    },
                  ]}
                />
              )}

            {matchPath(location.pathname, { path: [INTUNE_APP_CONFIGURATION_PATH] }) &&
              permissions.intune === 'manage' && (
                <DropdownMenu
                  id="app-configuration-create-menu"
                  title="POLICY_TEMPLATES.DEPLOY"
                  items={[
                    {
                      id: 'deploy-app-configuration-template',
                      label: 'POLICY_TEMPLATES.POLICY',
                      onClick: () =>
                        dispatch(subheader.actions.setGlobalValue({ openAppConfigurationPolicyDeploymentModal: true })),
                    },
                    {
                      id: 'deploy-app-configuration-baseline',
                      label: 'POLICY_TEMPLATES.BASELINE',
                      onClick: () =>
                        dispatch(
                          subheader.actions.setGlobalValue({ openAppConfigurationBaselineDeploymentModal: true }),
                        ),
                    },
                  ]}
                />
              )}

            {matchPath(location.pathname, { path: [INTUNE_APP_PROTECTION_TEMPLATES_PATH] }) &&
              permissions.intune === 'manage' && (
                <DropdownMenu
                  id="app-protection-templates-create-new-dropdown"
                  title="SECURE.CREATE_NEW"
                  items={[
                    {
                      id: 'create-new-app-protection-template',
                      label: 'POLICY_TEMPLATES.TEMPLATE',
                      onClick: () =>
                        dispatch(subheader.actions.setGlobalValue({ openCreateAppProtectionTemplate: true })),
                    },
                    {
                      id: 'create-new-app-protection-template-baseline',
                      label: 'POLICY_TEMPLATES.BASELINE',
                      onClick: () =>
                        dispatch(
                          subheader.actions.setGlobalValue({
                            openCreateAppProtectionBaseline: true,
                          }),
                        ),
                    },
                  ]}
                />
              )}

            {matchPath(location.pathname, { path: [INTUNE_APP_CONFIGURATION_TEMPLATES_PATH] }) &&
              permissions.intune === 'manage' && (
                <DropdownMenu
                  id="app-configuration-templates-create-new-dropdown"
                  title="SECURE.CREATE_NEW"
                  items={[
                    {
                      id: 'create-new-app-configuration-template',
                      label: 'POLICY_TEMPLATES.TEMPLATE',
                      onClick: () =>
                        dispatch(subheader.actions.setGlobalValue({ openCreateAppConfigurationTemplate: true })),
                    },
                    {
                      id: 'create-new-app-configuration-template-baseline',
                      label: 'POLICY_TEMPLATES.BASELINE',
                      onClick: () =>
                        dispatch(
                          subheader.actions.setGlobalValue({
                            openCreateAppConfigurationBaseline: true,
                          }),
                        ),
                    },
                  ]}
                />
              )}

            {matchPath(location.pathname, { path: [PURVIEW_DLP_PATH] }) &&
              permissions.purview === 'manage' &&
              isAuthorizedForPurview && (
                <DropdownMenu
                  id="dlp-deploy-menu"
                  title="POLICY_TEMPLATES.DEPLOY"
                  items={[
                    {
                      id: 'deploy-dlp-policy-template',
                      label: 'POLICY_TEMPLATES.POLICY',
                      onClick: () => dispatch(subheader.actions.setGlobalValue({ openDlpDeploymentModal: true })),
                    },
                    {
                      id: 'deploy-dlp-policies-baseline',
                      label: 'POLICY_TEMPLATES.BASELINE',
                      onClick: () =>
                        dispatch(subheader.actions.setGlobalValue({ openDlpBaselineDeploymentModal: true })),
                    },
                  ]}
                />
              )}

            {matchPath(location.pathname, { path: [PURVIEW_DLP_TEMPLATES_PATH] }) &&
              permissions.purview === 'manage' && (
                <DropdownMenu
                  id="dlp-templates-create-new-dropdown"
                  title="SECURE.CREATE_NEW"
                  items={[
                    {
                      id: 'create-new-dlp-template',
                      label: 'POLICY_TEMPLATES.TEMPLATE',
                      onClick: () => dispatch(subheader.actions.setGlobalValue({ openCreateDlpTemplate: true })),
                    },
                    {
                      id: 'create-new-dlp-template-baseline',
                      label: 'POLICY_TEMPLATES.BASELINE',
                      onClick: () => dispatch(subheader.actions.setGlobalValue({ openCreateDlpBaseline: true })),
                    },
                  ]}
                />
              )}

            {matchPath(location.pathname, { path: [PURVIEW_RETENTION_TEMPLATES_PATH] }) &&
              permissions.purview === 'manage' && (
                <DropdownMenu
                  id="retention-templates-create-new-dropdown"
                  title="SECURE.CREATE_NEW"
                  items={[
                    {
                      id: 'create-new-retention-template',
                      label: 'POLICY_TEMPLATES.TEMPLATE',
                      onClick: () => dispatch(subheader.actions.setGlobalValue({ openCreateRetentionTemplate: true })),
                    },
                    {
                      id: 'create-new-retention-template-baseline',
                      label: 'POLICY_TEMPLATES.BASELINE',
                      onClick: () => dispatch(subheader.actions.setGlobalValue({ openCreateRetentionBaseline: true })),
                    },
                  ]}
                />
              )}

            {matchPath(location.pathname, { path: [INTUNE_SCRIPTS_TEMPLATES_PATH] }) &&
              permissions.intune === 'manage' && (
                <DropdownMenu
                  id="scripts-templates-create-new-dropdown"
                  title="SECURE.CREATE_NEW"
                  items={[
                    {
                      id: 'create-new-scripts-template',
                      label: 'POLICY_TEMPLATES.TEMPLATE',
                      onClick: () => dispatch(subheader.actions.setGlobalValue({ openCreateScriptsTemplate: true })),
                    },
                    {
                      id: 'create-new-scripts-template-baseline',
                      label: 'POLICY_TEMPLATES.BASELINE',
                      onClick: () => dispatch(subheader.actions.setGlobalValue({ openCreateScriptsBaseline: true })),
                    },
                  ]}
                />
              )}

            {matchPath(location.pathname, { path: [INTUNE_REMEDIATION_SCRIPTS_TEMPLATES_PATH] }) &&
              permissions.intune === 'manage' && (
                <DropdownMenu
                  id="remediation-scripts-templates-create-new-dropdown"
                  title="SECURE.CREATE_NEW"
                  items={[
                    {
                      id: 'create-new-remediation-scripts-template',
                      label: 'POLICY_TEMPLATES.TEMPLATE',
                      onClick: () =>
                        dispatch(subheader.actions.setGlobalValue({ openCreateRemediationScriptsTemplate: true })),
                    },
                    {
                      id: 'create-new-remediation-scripts-template-baseline',
                      label: 'POLICY_TEMPLATES.BASELINE',
                      onClick: () =>
                        dispatch(subheader.actions.setGlobalValue({ openCreateRemediationScriptsBaseline: true })),
                    },
                  ]}
                />
              )}

            {matchPath(location.pathname, { path: [INTUNE_APPS_TEMPLATES_PATH] }) &&
              permissions.intune === 'manage' && (
                <DropdownMenu
                  id="app-template-create-new-dropdown"
                  title="SECURE.CREATE_NEW"
                  items={[
                    {
                      id: 'create-new-app-template',
                      label: 'POLICY_TEMPLATES.TEMPLATE',
                      onClick: () => dispatch(subheader.actions.setGlobalValue({ openCreateAppTemplate: true })),
                    },
                    {
                      id: 'create-new-app-template-baseline',
                      label: 'POLICY_TEMPLATES.BASELINE',
                      onClick: () =>
                        dispatch(
                          subheader.actions.setGlobalValue({
                            openCreateAppBaseline: true,
                          }),
                        ),
                    },
                  ]}
                />
              )}

            {window.location.pathname.match(/microsoft-csp/) && (
              <Button
                variant="light"
                onClick={() =>
                  dispatch(
                    subheader.actions.setGlobalValue({
                      showGdapMagicLinkModal: true,
                    }),
                  )
                }
              >
                <FormattedMessage id="INTEGRATION.CSP.CREATE_MAGIC_LINK" />
                <OverlayTrigger
                  overlay={
                    <Tooltip>
                      <FormattedMessage id="INTEGRATION.CSP.MAGIC_LINK_INFO" />
                    </Tooltip>
                  }
                >
                  <i className="fas fa-circle-info ms-2 font-color-2" />
                </OverlayTrigger>
              </Button>
            )}

            {permissions.secure === 'manage' &&
              matchPath(location.pathname, { path: SECURE_COMPLIANCE_ASSESSMENTS_PATH }) && (
                <Button
                  variant="primary"
                  onClick={() =>
                    history.push(generatePath(SECURE_COMPLIANCE_ASSESSMENT_PATH, { assessmentId: 'create' }))
                  }
                >
                  <FormattedMessage id="SECURE.COMPLIANCE_CREATE_ASSESSMENT" />
                </Button>
              )}
            {permissions.secure === 'manage' &&
              matchPath(location.pathname, { path: SECURE_COMPLIANCE_ASSESSMENT_PATH }) && (
                <FormButtons
                  formId="compliance-assessment-upsert"
                  submitButtonMessageId="SECURE.COMPLIANCE_UPSERT_ASSESSMENT_SAVE"
                />
              )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default injectIntl(SubHeader)
